import { useEffect } from 'react'
import { useBlocker } from '@cedarjs/router'
import { useDispatch } from 'src/hooks/useDispatch'
import { openModal } from 'src/slices/modal'

/**
 * ! If you're using this hook in a component, make sure to include the ConfirmationModal in the component tree
 * This hook is used to prevent navigation away from a page if there are unsaved changes.
 * It uses the useBlocker hook from @cedarjs/router to block navigation and a beforeunload event to show a warning modal.
 * @param hasUnsavedChanges - A boolean indicating if there are unsaved changes.
 * @returns A blocker object that can be used to block navigation.
 */
const usePreventNavigationForUnsavedChanges = ({
  hasUnsavedChanges,
}: {
  hasUnsavedChanges: boolean
}) => {
  const dispatch = useDispatch()
  const blocker = useBlocker({ when: hasUnsavedChanges })

  // Add beforeunload warning for unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        event.preventDefault()
        event.returnValue = ''
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [hasUnsavedChanges])

  useEffect(() => {
    if (blocker.state === 'BLOCKED') {
      dispatch(
        openModal({
          name: 'confirmationModal',
          data: {
            discriminator: 'unsaved-changes-warning',
          },
        })
      )
    }
  }, [blocker.state])

  const confirmNavigation = () => {
    blocker.confirm()
  }

  const denyNavigation = () => {
    blocker.abort()
  }

  return { confirmNavigation, denyNavigation }
}

export default usePreventNavigationForUnsavedChanges
